// This is called a "splat route" and as it's in the root `/app/routes/`
// directory, it's a catchall. If no other routes match, this one will and we
// can know that the user is hitting a URL that doesn't exist. By throwing a
// 404 from the loader, we can force the error boundary to render which will
// ensure the user gets the right status code and we can display a nicer error
// message for them than the Remix and/or browser default.
//
// However, we first check if there's a dynamic page in the database with this slug.
// This allows pages created in the admin to be served at root-level URLs like /werkwijze

import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type LoaderFunctionArgs, Link, useLocation } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { BlockRenderer } from '#app/components/block-renderer.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { buildMeta, buildBreadcrumbJsonLd } from '#app/utils/meta.ts'
import { type Route } from './+types/$.ts'

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => {
		const pages = await prisma.page.findMany({
			where: { published: true, pageType: 'page' },
			select: { slug: true, updatedAt: true },
		})
		return pages.map((page) => ({
			route: `/${page.slug}`,
			lastmod: page.updatedAt?.toISOString(),
			priority: 0.6,
		}))
	}),
}

export const meta: Route.MetaFunction = ({ data, params }) => {
	if (!data?.page) {
		return [{ title: 'Pagina niet gevonden' }]
	}
	const slug = params['*'] || ''
	return [
		...buildMeta({
			title: data.page.metaTitle || data.page.title,
			description: data.page.metaDescription || '',
			url: `/${slug}`,
			image: data.page.ogImage,
		}),
		buildBreadcrumbJsonLd([
			{ name: 'Home', url: '/' },
			{ name: data.page.title, url: `/${slug}` },
		]),
	]
}

export async function loader({ params, request }: LoaderFunctionArgs) {
	// Get the slug from the splat route (e.g., "werkwijze" or "over-ons")
	const slug = params['*']
	const url = new URL(request.url)
	const serviceSlug = url.searchParams.get('service')

	// If there's a slug, check if it's a dynamic page
	if (slug) {
		const page = await prisma.page.findUnique({
			where: { slug, published: true },
			include: {
				blocks: {
					orderBy: { order: 'asc' },
				},
			},
		})

		if (page) {
			// Fetch services, sectors, and case studies for dynamic blocks
			const [services, sectors, caseStudies] = await Promise.all([
				prisma.service.findMany({
					select: { id: true, name: true, slug: true, description: true },
					orderBy: { name: 'asc' },
				}),
				prisma.sector.findMany({
					select: { id: true, name: true, slug: true },
					orderBy: { name: 'asc' },
				}),
				prisma.caseStudy.findMany({
					where: { published: true },
					select: {
						id: true,
						title: true,
						slug: true,
						client: true,
						imageUrl: true,
						imageAlt: true,
						featured: true,
						order: true,
						heroVideoMode: true,
						heroVideoPlaybackId: true,
						services: {
							select: { id: true, name: true, slug: true },
						},
						sectors: {
							select: { id: true, name: true, slug: true },
						},
					},
					orderBy: [{ order: 'asc' }, { id: 'asc' }],
				}),
			])

			return {
				page,
				services,
				sectors,
				caseStudies: caseStudies.map((cs) => ({
					...cs,
					videoMode: (cs.heroVideoMode as 'none' | 'hover' | 'autoplay') ?? 'none',
					videoPlaybackId: cs.heroVideoPlaybackId ?? undefined,
				})),
				currentServiceSlug: serviceSlug,
				ctaConfig: {
					mode: page.ctaMode,
					title: page.ctaTitle,
					buttonText: page.ctaButtonText,
					buttonLink: page.ctaButtonLink,
					imageUrl: page.ctaImageUrl,
					imageAlt: page.ctaImageAlt,
					highlightWords: page.ctaHighlightWords,
					fontSizePreset: page.ctaFontSizePreset,
					showButton: page.ctaShowButton,
				},
			}
		}
	}

	// No dynamic page found, throw 404
	throw new Response('Not found', { status: 404 })
}

export function action() {
	throw new Response('Not found', { status: 404 })
}

export default function DynamicPage({ loaderData }: Route.ComponentProps) {
	// If we have page data, render the dynamic page
	if (loaderData?.page) {
		const { page, services, sectors, caseStudies, currentServiceSlug } = loaderData
		return (
			<main>
				<BlockRenderer
					blocks={page.blocks}
					services={services}
					sectors={sectors}
					caseStudies={caseStudies}
					currentServiceSlug={currentServiceSlug}
				/>
			</main>
		)
	}

	// Fallback to error boundary (should not happen due to loader throwing 404)
	return <ErrorBoundary />
}

export function ErrorBoundary() {
	const location = useLocation()
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: () => (
					<div className="flex flex-col gap-6">
						<div className="flex flex-col gap-3">
							<h1>Deze pagina kon niet worden gevonden:</h1>
							<pre className="text-body-lg break-all whitespace-pre-wrap">
								{location.pathname}
							</pre>
						</div>
						<Link to="/" className="text-body-md underline">
							<Icon name="arrow-left">Terug naar home</Icon>
						</Link>
					</div>
				),
			}}
		/>
	)
}
